'use client'

import type { ReactNode } from 'react'
import { useIsMobile } from '@/lib/use-is-mobile'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/popover'
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from '@/ui/drawer'
import { cn } from '@/lib/tailwind'

interface ResponsiveSelectorProps {
  /** The trigger element that opens the selector */
  trigger: ReactNode
  /** Controlled open state */
  open: boolean
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void
  /** Content rendered inside the popover (desktop) or drawer (mobile) */
  children: ReactNode
  /** Additional className for the content container */
  contentClassName?: string
  /** Accessible label for the drawer (screen readers only) */
  ariaLabel?: string
  /** Popover alignment (desktop only) */
  align?: 'start' | 'center' | 'end'
  /** Popover side offset (desktop only) */
  sideOffset?: number
}

/**
 * Renders children inside a Popover on desktop or a bottom Drawer on mobile.
 * Uses the 640px breakpoint (useIsMobile) to switch between modes.
 *
 * Desktop: Radix Popover with configurable alignment.
 * Mobile: Vaul Drawer with drag handle, max 60vh height, touch-friendly padding.
 */
export function ResponsiveSelector({
  trigger,
  open,
  onOpenChange,
  children,
  contentClassName,
  ariaLabel = 'Selector',
  align = 'start',
  sideOffset = 4,
}: ResponsiveSelectorProps) {
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent
          className={cn('max-h-[60vh] px-4 pb-6', contentClassName)}
          aria-describedby={undefined}
        >
          <DrawerTitle className="sr-only">{ariaLabel}</DrawerTitle>
          {children}
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align={align}
        sideOffset={sideOffset}
        className={cn('p-2', contentClassName)}
      >
        {children}
      </PopoverContent>
    </Popover>
  )
}
